import React, { FC, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Tab,
    Tabs,
    IconButton,
    Stack,
    Button,
    useTheme,
    alpha,
    Paper
} from '@mui/material';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import PublicIcon from '@mui/icons-material/Public';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BoltIcon from '@mui/icons-material/Bolt';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { motion, AnimatePresence } from 'framer-motion';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const features = [
    {
        icon: <DeveloperBoardIcon />,
        title: 'Powerful Developer Tools',
        description: 'Industry-leading SDKs and detailed API documentation for rapid integration'
    },
    {
        icon: <SecurityIcon />,
        title: 'Advanced KYC Solutions',
        description: 'Enterprise-grade identity verification with 98% pass rates'
    },
    {
        icon: <GavelIcon />,
        title: 'Global Compliance',
        description: 'Automated compliance across 180+ jurisdictions worldwide'
    },
    {
        icon: <PublicIcon />,
        title: 'Borderless Payments',
        description: 'Accept payments in 30+ fiat currencies with real-time settlement'
    },
    {
        icon: <VerifiedUserIcon />,
        title: 'Trusted KYB Verification',
        description: 'Secure and verified onboarding with industry-standard KYB checks'
      },
      
    {
        icon: <AccountBalanceWalletIcon />,
        title: 'Enterprise Wallet System',
        description: 'Military-grade security with institutional-scale infrastructure'
    }
];

const platforms = [
    {
        icon: <BoltIcon />,
        title: 'Instant Payment Onramp',
        description: 'Industry-best rates for fiat to crypto conversion',
        stats: '30+ Fiat Currencies | 400+ Digital Assets',
        color: '#FF6B00'
    },
    {
        icon: <CurrencyExchangeIcon />,
        title: 'Seamless Payment Offramp',
        description: 'Lightning-fast crypto to fiat liquidation',
        stats: '30+ Fiat Currencies | 400+ Digital Assets',
        color: '#FF6B00'
    }
];

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`feature-tabpanel-${index}`}
            aria-labelledby={`feature-tab-${index}`}
            {...other}
        >
            <AnimatePresence mode="wait">
                {value === index && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <Box sx={{ py: 4 }}>{children}</Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export const HomeFeatureShowcase: FC = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState(1);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    return (
        <Box

          id="features"
            sx={{
                py: { xs: 8, md: 15 },
                backgroundColor: '#0B0F18',
                color: 'common.white',
                overflow: 'hidden'
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ position: 'relative' }}>
                    {/* Background Gradient Effects */}
                    <Box
                     
                        sx={{
                            position: 'absolute',
                            top: -200,
                            right: -200,
                            width: 500,
                            height: 500,
                            background: 'radial-gradient(circle, rgba(255,107,0,0.15) 0%, rgba(255,107,0,0) 70%)',
                            filter: 'blur(60px)',
                            zIndex: 0
                        }}
                    />

                    {/* Main Content */}
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            centered
                            sx={{
                                mb: 6,
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'primary.main'
                                },
                                '& .MuiTab-root': {
                                    color: 'grey.500',
                                    fontSize: '1.125rem',
                                    fontWeight: 600,
                                    '&.Mui-selected': {
                                        color: 'primary.main'
                                    }
                                }
                            }}
                        >
                            <Tab label="Our Platform" />
                            <Tab label="Core Features" />
                        </Tabs>

                        <TabPanel value={activeTab}
                            index={0}>
                            <Stack spacing={4}
                                alignItems="center"
                                textAlign="center"
                                sx={{ mb: 8 }}>
                                <Typography variant="h3"
                                    sx={{ fontWeight: 700 }}>
                                    Enterprise-Grade Payment Infrastructure
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '1.125rem',
                                        maxWidth: '800px',
                                        color: alpha('#fff', 0.7)
                                    }}
                                >
                                    Transform your business with institutional-grade payment solutions - Unlock the best rates and fastest settlements in the industry
                                </Typography>
                            </Stack>

                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={4}
                                sx={{
                                    '& > *': {
                                        flex: 1
                                    }
                                }}
                            >
                                {platforms.map((platform, index) => (
                                    <motion.div
                                        key={platform.title}
                                        initial={{ opacity: 0, x: index === 0 ? -50 : 50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.2 }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 4,
                                                height: '100%',
                                                backgroundColor: alpha('#1E2837', 0.6),
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: 3,
                                                border: '1px solid',
                                                borderColor: alpha('#fff', 0.1),
                                                transition: 'all 0.3s ease-in-out',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    borderColor: platform.color,
                                                    '& .platform-icon': {
                                                        transform: 'scale(1.1)',
                                                    }
                                                }
                                            }}
                                        >
                                            <Stack spacing={3}>
                                                <Box
                                                    className="platform-icon"
                                                    sx={{
                                                        width: 80,
                                                        height: 80,
                                                        borderRadius: 2,
                                                        backgroundColor: alpha(platform.color, 0.1),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'transform 0.3s ease-in-out',
                                                        mx: 'auto'
                                                    }}
                                                >
                                                    {React.cloneElement(platform.icon, {
                                                        sx: { fontSize: 40, color: platform.color }
                                                    })}
                                                </Box>
                                                <Box>
                                                    <Typography variant="h4"
                                                        sx={{ mb: 2, fontWeight: 700, color: 'white' }}>
                                                        {platform.title}
                                                    </Typography>
                                                    <Typography sx={{ mb: 2, color: alpha('#fff', 0.7) }}>
                                                        {platform.description}
                                                    </Typography>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            color: platform.color,
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {platform.stats}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Paper>
                                    </motion.div>
                                ))}
                            </Stack>
                        </TabPanel>

                        <TabPanel value={activeTab}
                            index={1}>
                            <Stack spacing={4}
                                alignItems="center"
                                textAlign="center"
                                sx={{ mb: 8 }}>
                                <Typography variant="h3"
                                    sx={{ fontWeight: 700 }}>
                                    Built for Scale and Security
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '1.125rem',
                                        maxWidth: '800px',
                                        color: alpha('#fff', 0.7)
                                    }}
                                >
                                    Enterprise solutions trusted by leading businesses worldwide for reliable paymentt operations
                                </Typography>
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, 1fr)',
                                        md: 'repeat(3, 1fr)'
                                    },
                                    gap: 4
                                }}
                            >
                                {features.map((feature, index) => (
                                    <motion.div
                                        key={feature.title}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 3,
                                                height: '100%',
                                                backgroundColor: alpha('#1E2837', 0.6),
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: alpha('#fff', 0.1),
                                                transition: 'all 0.3s ease-in-out',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    borderColor: 'primary.main',
                                                    '& .feature-arrow': {
                                                        transform: 'translateX(4px)',
                                                        opacity: 1
                                                    }
                                                }
                                            }}
                                        >
                                            <Stack spacing={2}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 1.5,
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                                        }}
                                                    >
                                                        {React.cloneElement(feature.icon, {
                                                            sx: { fontSize: 24, color: 'primary.main' }
                                                        })}
                                                    </Box>
                                                    <IconButton
                                                        className="feature-arrow"
                                                        size="small"
                                                        sx={{
                                                            color: 'primary.main',
                                                            opacity: 0,
                                                            transition: 'all 0.3s ease-in-out'
                                                        }}
                                                    >
                                                        <ArrowForwardIcon />
                                                    </IconButton>
                                                </Box>
                                                <Typography variant="h6"
                                                    sx={{ fontWeight: 600, color: 'white' }}>
                                                    {feature.title}
                                                </Typography>
                                                <Typography sx={{ color: alpha('#fff', 0.7) }}>
                                                    {feature.description}
                                                </Typography>
                                            </Stack>
                                        </Paper>
                                    </motion.div>
                                ))}
                            </Box>
                        </TabPanel>

                        <Box sx={{ mt: 8, textAlign: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                href="https://cal.com/support-team-qsszsx"
                                target="_blank"
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontSize: '1.125rem'
                                }}
                            >
                                Start Building Now
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}; 